import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/atoms";
import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useTranslation } from "@/core/i18n";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import { PlatformSearchModal } from "@/modules/onboarding/components/PlatformSearchModal/PlatformSearchModal";
import { useOnboardingContext } from "@/modules/onboarding/context";
import { updateUserIfAuthenticated } from "@/modules/onboarding/utils/updateUserIfAuthenticated";
import { useSignUpLocationSuggestion } from "@/modules/onboarding/utils/useSignUpLocationSuggestion";
import {
  getMultiPlatformPlatformByPlatformIdLocations,
  getPlatformCurrent,
  getReferralCodesByReferralCode,
} from "api/methods";
import { LocationPickerScreen } from "modules/onboarding/screens/LocationPickerScreen";

export default function V3LocationPicker() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { platformSelection, referral, setPlatformId, updatePlatformSelection } =
    useOnboardingContext();

  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isResolvingPlatformId, setIsResolvingPlatformId] = useState(!platformSelection.platformId);

  const {
    data: locations = [],
    isPending: isLoading,
    error,
  } = useQuery({
    ...getMultiPlatformPlatformByPlatformIdLocations.keyed({
      platformId: platformSelection.platformId ?? "",
    }),
    select: ({ locations }) => {
      if (!locations || locations.length === 0) {
        console.error(`attempted to find locations for`, platformSelection, `and got none back`);
        throw new Error("No locations found");
      }
      return locations;
    },
    enabled: !!platformSelection.platformId,
  });

  const { data: referralCodeData } = useQuery({
    ...getReferralCodesByReferralCode.keyed({
      referralCode: referral.referralCode ?? "",
    }),
    enabled: !!referral.referralCode,
    staleTime: 0,
  });
  const referralLocationId = referralCodeData?.platformLocation?.id?.value;

  // Asks for the browser position only when the patient still has a choice to make: a referral
  // location or a single branch auto-advances, and a returning patient keeps their earlier pick.
  const { data: locationSuggestion } = useSignUpLocationSuggestion(
    platformSelection.platformId ?? "",
    !!platformSelection.platformId &&
      locations.length > 1 &&
      !platformSelection.locationId &&
      (!referral.referralCode || (referralCodeData !== undefined && !referralLocationId)),
  );

  const handleLocationSelect = useCallback(
    async (locationId: string) => {
      // Only update if the locationId is actually different
      if (platformSelection.locationId !== locationId) {
        updatePlatformSelection({ locationId });
      }

      // Persist for authenticated users so /platform-location succeeds after redirects.
      await updateUserIfAuthenticated({ platformLocationId: locationId });

      navigate("/signin/source");
    },
    [updatePlatformSelection, navigate, platformSelection.locationId],
  );

  // Auto-advance:
  // - If referral code contains a location, use it
  // - If only one location exists, auto-select it
  useEffect(() => {
    if (!platformSelection.platformId) return;

    if (referralLocationId) {
      void handleLocationSelect(referralLocationId);
      return;
    }

    if (locations.length === 1) {
      void handleLocationSelect(locations[0].id);
    }
  }, [handleLocationSelect, locations, platformSelection.platformId, referralLocationId]);

  const handlePlatformSelect = async (platformId: string) => {
    setPlatformId(platformId);
    setShowSearchModal(false);
  };

  const handleFindAnotherPractice = () => {
    setShowSearchModal(true);
  };

  useEffect(() => {
    if (platformSelection.platformId) {
      // edge case, just in case
      // if platform id becomes available somehow after the initial mount
      setIsResolvingPlatformId(false);
    } else {
      setIsResolvingPlatformId(true);

      getPlatformCurrent()
        .then((platform) => {
          if (platform?.id) {
            setPlatformId(platform.id);
            return;
          }
          navigate("/signin");
        })
        .catch(() => {
          navigate("/signin");
        })
        .finally(() => {
          setIsResolvingPlatformId(false);
        });
    }
  }, [navigate, platformSelection.platformId, setPlatformId]);

  if (isResolvingPlatformId) {
    // Avoid flashing the picker while we try to infer platformId for authenticated redirects.
    return null;
  }
  if (error) {
    return (
      <MobileViewportContainer header={<OnboardingBackButton />}>
        <div className="flex flex-col items-center justify-center px-8 py-16">
          <div className="mb-8 text-center text-destructive">{t("v3LocationPicker.loadError")}</div>
          <Button fullWidth onClick={() => navigate("/signin")} style="secondary">
            {t("v3LocationPicker.backToSignIn")}
          </Button>
        </div>
      </MobileViewportContainer>
    );
  }

  return (
    <>
      <PlatformSearchModal
        open={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onPlatformSelect={handlePlatformSelect}
        canClose={!!platformSelection.platformId}
      />

      <MobileViewportContainer header={<OnboardingBackButton />}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        >
          <LocationPickerScreen
            locations={locations}
            selectedLocation={platformSelection.locationId}
            suggestion={locationSuggestion}
            onLocationSelect={handleLocationSelect}
            isLoading={isLoading && !!platformSelection.platformId}
            loadingMessage={t("v3LocationPicker.loadingLocations")}
            hideFindNearby={!platformSelection.canReselectPlatform}
            onFindPracticeClick={handleFindAnotherPractice}
          />
        </motion.div>
      </MobileViewportContainer>
    </>
  );
}
